import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronDown, Hash, PlusCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTheme } from '@/managers/ThemeProvider';
import { authManager, TIERS } from '@/managers/AuthManager';
import { usePanelManager, RightSidebarViews } from '@/managers/PanelManager';
import { useConversation } from '@/managers/ConvoManager';
import { workspaceManager } from '@/managers/workspaceManager';
import { areWorkspaceContextIdsEqual, isDefaultWorkspaceContextId, isPrivateWorkspaceContextId, normalizeWorkspaceContextId } from '@/utils/workspaceContext';
import { canAccessWorkspaces } from '@/utils/workspaceAccess';
import { isWorkspaceBackendEnabled } from '@/utils/setProdOrDev';

const WORKSPACE_LIMITS = {
  [TIERS.FREE]: 3,
  [TIERS.PLUS]: 3,
  [TIERS.PRO]: 3,
  [TIERS.EVOK]: 10,
  [TIERS.EVOK_ULTRA]: 100,
  [TIERS.BETA]: Infinity,
  [TIERS.ADMIN]: Infinity,
};

const WorkspaceSwitcher = React.memo(({ hasNewCompletion = false, hasPendingApprovalTask = false }) => {
  const { isLightTheme } = useTheme();
  const { currentConversation, createConversation } = useConversation();
  const { setRightSidebarView } = usePanelManager();

  const [wsState, setWsState] = useState(workspaceManager.getState());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [userTier, setUserTier] = useState(authManager.getTier());
  const [authState, setAuthState] = useState(authManager.getState());
  const isWorkspaceBackendEnabledFlag = isWorkspaceBackendEnabled();

  const { workspaces: allManagerWorkspaces, activeWorkspaceId } = wsState;
  const canUseWorkspaces = canAccessWorkspaces(authState.user);

  useEffect(() => {
    const unsubWorkspaces = workspaceManager.subscribe(setWsState);
    const unsubAuth = authManager.subscribe((state) => {
      setUserTier(state.tier);
      setAuthState(state);
    });

    return () => {
      unsubWorkspaces();
      unsubAuth();
    };
  }, []);

  useEffect(() => {
    if (!canUseWorkspaces && isDropdownOpen) {
      setIsDropdownOpen(false);
    }
  }, [canUseWorkspaces, isDropdownOpen]);

  useEffect(() => {
    if (!canUseWorkspaces || !isWorkspaceBackendEnabledFlag || !currentConversation?.id) {
      return;
    }

    const conversationWorkspaceId = normalizeWorkspaceContextId(currentConversation.workspaceContextId);
    if (isPrivateWorkspaceContextId(conversationWorkspaceId)) {
      return;
    }
    if (!areWorkspaceContextIdsEqual(activeWorkspaceId, conversationWorkspaceId)) {
      workspaceManager.setActiveWorkspace(conversationWorkspaceId);
    }
  }, [activeWorkspaceId, canUseWorkspaces, currentConversation, isWorkspaceBackendEnabledFlag]);

  const workspaces = useMemo(() => allManagerWorkspaces || [], [allManagerWorkspaces]);

  const createdWorkspaceCount = useMemo(
    () => workspaces.filter((workspace) => !isDefaultWorkspaceContextId(workspace.id)).length,
    [workspaces]
  );
  const workspaceLimit = WORKSPACE_LIMITS[userTier] ?? 0;
  const showFreeWorkspaceMeter = userTier === TIERS.FREE || userTier === TIERS.PLUS;

  const currentWorkspaceId = normalizeWorkspaceContextId(currentConversation?.workspaceContextId || activeWorkspaceId);
  const isPrivateWorkspaceActive = isPrivateWorkspaceContextId(currentWorkspaceId);
  const selectedWorkspace = isPrivateWorkspaceActive
    ? null
    : (workspaces.find((workspace) => areWorkspaceContextIdsEqual(workspace.id, currentWorkspaceId)) || wsState.activeWorkspaceDetail || null);
  const isWorkspaceActive = !isPrivateWorkspaceActive && !isDefaultWorkspaceContextId(currentWorkspaceId);

  const handleDropdownToggle = () => {
    if (!canUseWorkspaces) {
      return;
    }

    if (!isDropdownOpen && isWorkspaceBackendEnabledFlag) {
      workspaceManager.fetchWorkspaces();
    }

    setIsDropdownOpen((prev) => !prev);
  };

  const handleWorkspaceSelect = useCallback(async (workspaceId) => {
    const nextWorkspaceId = normalizeWorkspaceContextId(workspaceId);
    const currentContextId = normalizeWorkspaceContextId(currentConversation?.workspaceContextId);
    workspaceManager.setActiveWorkspace(nextWorkspaceId);
    setRightSidebarView(RightSidebarViews.WORKSPACE);
    setIsDropdownOpen(false);

    // If switching to a different workspace, start a fresh conversation scoped to it
    // so chat history from the previous workspace doesn't bleed through.
    if (!areWorkspaceContextIdsEqual(currentContextId, nextWorkspaceId)) {
      await createConversation({ workspaceContextId: nextWorkspaceId });
    }
  }, [currentConversation, createConversation, setRightSidebarView]);

  const handleClickOutside = useCallback((event) => {
    if (!event.target.closest('.workspace-dropdown')) {
      setIsDropdownOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }

    return undefined;
  }, [isDropdownOpen, handleClickOutside]);

  const handleInitiateCreate = () => {
    if (!canUseWorkspaces || !isWorkspaceBackendEnabledFlag) {
      return;
    }

    window.dispatchEvent(new CustomEvent('open-create-workspace-modal'));
    setIsDropdownOpen(false);
  };

  const formatDisplayName = (workspace) => {
    if (!workspace) {
      return isPrivateWorkspaceActive ? 'Incognito' : 'Workspace';
    }

    if (workspace.isDefault) {
      return 'Main Workspace';
    }

    return workspace.workspaceName || workspace.id;
  };

  if (!canUseWorkspaces) {
    return null;
  }

  return (
    <div className="relative workspace-dropdown">
      <button
        onClick={handleDropdownToggle}
        className={`relative flex min-h-[34px] max-w-[220px] items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs transition-all duration-150 ${
          isWorkspaceActive
            ? isLightTheme
              ? 'border-white/30 bg-white/20 text-white hover:bg-white/25'
              : 'border-border-color bg-surface-dark text-on-surface hover:bg-surface'
            : isLightTheme
              ? 'border-transparent text-white/80 hover:bg-white/20 hover:text-white'
              : 'border-transparent text-on-surface-dim hover:bg-surface-dark hover:text-on-surface'
        }`}
      >
        {hasPendingApprovalTask && (
          <motion.span
            className="absolute left-1 top-1/2 block h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-yellow-400 ring-1 ring-surface-darker"
            animate={{ scale: [1, 1.4, 1] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            title="Workspace awaiting approval"
          />
        )}
        <Hash className={`h-3.5 w-3.5 flex-shrink-0 ${isWorkspaceActive ? 'text-current' : ''}`} />
        <div className="min-w-0 flex-1 text-left leading-none">
          <div className={`truncate text-[7px] font-semibold tracking-[0.16em] ${isLightTheme ? 'text-white/55' : 'text-on-surface-dim/80'}`}>
            WORKSPACE
          </div>
          <div className={`mt-0.5 truncate text-[10px] font-semibold tracking-wide ${isLightTheme ? 'text-primary' : 'text-primary-light'}`}>
            {formatDisplayName(selectedWorkspace)}
          </div>
        </div>
        <ChevronDown className={`h-3.5 w-3.5 flex-shrink-0 self-center transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : 'rotate-0'}`} />
        {hasNewCompletion && <span className="absolute right-1 top-1 block h-1.5 w-1.5 rounded-full bg-green-400 ring-1 ring-surface" />}
      </button>

      {isDropdownOpen && (
        <div
          className={`absolute right-0 top-full z-50 mt-1 flex w-64 flex-col rounded-md border shadow-lg ${
            isLightTheme
              ? 'border-white/20 bg-white shadow-black/20'
              : 'border-border-color bg-surface-dark shadow-black/40'
          }`}
        >
          <div className="max-h-[50vh] overflow-y-auto py-1">
            {!isWorkspaceBackendEnabledFlag ? (
              <div className={`mx-2 mb-1 rounded-md border p-2 text-[11px] leading-snug ${isLightTheme ? 'border-gray-200 bg-gray-50 text-gray-600' : 'border-border-color bg-surface text-on-surface-dim'}`}>
                Workspace controls are visible for allowlisted users, but the backend is disabled in this build.
              </div>
            ) : null}

            {isWorkspaceBackendEnabledFlag && (
              <button
                onClick={handleInitiateCreate}
                className={`flex w-full items-center gap-1.5 truncate px-3 py-1.5 text-left text-xs font-medium transition-colors duration-150 ${
                  isLightTheme ? 'text-primary hover:bg-primary/10' : 'text-primary-light hover:bg-primary-light/10'
                }`}
              >
                <PlusCircle className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate text-[10px] font-semibold tracking-wide">+ New Workspace</span>
                {showFreeWorkspaceMeter && (
                  <span className={`ml-auto rounded-full px-1.5 py-0.5 text-[9px] font-semibold tracking-wide ${isLightTheme ? 'bg-gray-100 text-gray-500' : 'bg-background-dark text-on-surface-dim'}`}>
                    {createdWorkspaceCount + 1}/{workspaceLimit}
                  </span>
                )}
              </button>
            )}

            {workspaces.map((workspace) => (
              <button
                key={workspace.id}
                onClick={() => handleWorkspaceSelect(workspace.id)}
                className={`flex w-full items-center gap-1.5 truncate px-3 py-1.5 text-left text-xs font-medium transition-colors duration-150 ${
                  areWorkspaceContextIdsEqual(currentWorkspaceId, workspace.id)
                    ? isLightTheme
                      ? 'bg-primary/10 text-primary'
                      : 'bg-primary/20 text-primary-light'
                    : isLightTheme
                      ? 'text-gray-700 hover:bg-gray-100'
                      : 'text-on-surface-dim hover:bg-surface hover:text-on-surface'
                }`}
                title={formatDisplayName(workspace)}
              >
                <Hash className="h-3 w-3 flex-shrink-0" />
                <span className="truncate text-[10px] font-semibold tracking-wide">{formatDisplayName(workspace)}</span>
                {areWorkspaceContextIdsEqual(currentWorkspaceId, workspace.id) && <div className={`ml-auto h-1.5 w-1.5 flex-shrink-0 rounded-full ${isLightTheme ? 'bg-primary' : 'bg-primary-light'}`} />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

WorkspaceSwitcher.displayName = 'WorkspaceSwitcher';

export default WorkspaceSwitcher;